"use client";

import { useToast } from "@/context/ToastContext";
import {
    importMatchCountryStateAction,
    importMatchCountryStateParamsType,
    importMatchCountryStateType,
    manageImportCandidatesAction,
    ManageImportCandidatesResultType,
    ManageImportCandidatesType,
    manageImportContractsAction,
    ManageImportContractsResultType,
    ManageImportContractsType,
} from "@/utils/serverActions/import/importActions";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { handleHookError } from "utils/helpers/handleError";
import {
  chunkRows,
  IMPORT_CHUNK_ROWS,
  mergeCandidatesResults,
  mergeContractsResults,
} from "@/utils/import/importChunks";
import { useCountriesStatic, useGenderStatic, useInvoiceFrequencyStatic, useInvoiceModeStatic, useInvoiceTimeModeStatic, useRatePeriodStatic } from "../static/staticHooks";
import { useSubscriptionPlanInfo } from "../subscriptions/subscriptionHooks";
export const queryKeys = {
  importMatchCountryState: (params: importMatchCountryStateParamsType) => ["importMatchCountryState", params] as const,
  importCandidates: (params: ManageImportCandidatesType) => ["importCandidates", params] as const,
  importContracts: (params: ManageImportContractsType) => ["importContracts", params] as const,
};


export const useImportMatchCountryState = (params: importMatchCountryStateParamsType) => {
  const { showToast } = useToast();
  return useQuery({
    queryKey: queryKeys.importMatchCountryState(params),
    queryFn: async () => {
      const data = await importMatchCountryStateAction(params);
      console.log("data in useImportMatchCountryState", JSON.stringify(data));
      return data;
    },
    onError: (error: Error) => {
      handleHookError(showToast, error, "Failed to load payments. Please try again.");
    },
    // enabled: params.p_country_name.length > 0,
  } as UseQueryOptions<importMatchCountryStateType[]>);
};


/** Called after every chunk of an import with the rows done and the rows in all. */
export type ImportProgressHandlerType = (done: number, total: number) => void;

/** Message when an import stops in the middle for an error (the real one does not reach the browser). */
const INTERRUPTED_MESSAGE = "Hubo un error de conexión o del servidor mientras se importaba.";

export const useImportCandidates = (options?: { onProgress?: ImportProgressHandlerType }) => {
  const { showToast } = useToast();

  return useMutation({
      // The rows go in chunks of IMPORT_CHUNK_ROWS.candidates, one after the other, each in its
      // own transaction; the results are added up, with the rows numbered as in the file.
      mutationFn: async (updateData: ManageImportCandidatesType): Promise<ManageImportCandidatesResultType> => {
      const total = updateData.p_data.length;
      const results: ManageImportCandidatesResultType[] = [];
      const offsets: number[] = [];
      let done = 0;

      for (const chunk of chunkRows(updateData.p_data, IMPORT_CHUNK_ROWS.candidates)) {
        let result: ManageImportCandidatesResultType;
        try {
          result = await manageImportCandidatesAction({ ...updateData, p_data: chunk });
        } catch (error) {
          if (results.length === 0) throw error;
          return mergeCandidatesResults(results, offsets, { interrupted: INTERRUPTED_MESSAGE, rowsNotRun: total - done });
        }

        if (result.refused) {
          // Nothing ran: the screen explains why. In the middle: what ran stays, and says so.
          if (results.length === 0) return result;
          return mergeCandidatesResults(results, offsets, {
            refused: result.refused,
            usage: result.usage,
            block: result.block,
            rowsNotRun: total - done,
          });
        }

        results.push(result);
        offsets.push(done);
        done += chunk.length;
        options?.onProgress?.(done, total);
      }

      return mergeCandidatesResults(results, offsets);
    },
    onSuccess: (result: ManageImportCandidatesResultType) => {
      // A refused import wrote nothing; the screen explains why.
      if (result.refused && result.total_processed === 0) return;

      const { total_processed, successful_imports, updated_found, skipped_found, errors_found, not_added_candidates } = result;

      let message = `Importación completada: ${successful_imports}/${total_processed} candidatos importados exitosamente`;

      if (updated_found > 0) {
        message += `. ${updated_found} actualizados`;
      }

      if (skipped_found > 0) {
        message += `. ${skipped_found} omitidos`;
      }

      if (errors_found > 0) {
        message += `. ${errors_found} errores encontrados`;
      }

      // Stopped in the middle: what ran stays, and the screen says why.
      const stopped = Boolean(result.refused || result.interrupted);
      if (stopped) {
        message = `Importación incompleta: ${successful_imports + updated_found}/${total_processed + (result.rowsNotRun ?? 0)} filas procesadas`;
      }
      
      showToast({              
        description: message,
        variant: stopped ? "destructive" : "success",
      });
      
      // Log detailed results for debugging
      console.log("Import results:", result);
      
      if (not_added_candidates.length > 0) {
        console.log("Candidates not added:", not_added_candidates);
      }
    },
    onError: (error: Error) => {
      handleHookError(showToast, error, "Hubo un error al importar los candidatos.");
    },
  });
}; 

export const useImportContracts = (options?: { onProgress?: ImportProgressHandlerType }) => {
  const { showToast } = useToast();

  return useMutation({
      // Chunks of IMPORT_CHUNK_ROWS.contracts rows, one after the other; see useImportCandidates.
      mutationFn: async (updateData: ManageImportContractsType): Promise<ManageImportContractsResultType> => {
      const total = updateData.p_data.length;
      const results: ManageImportContractsResultType[] = [];
      const offsets: number[] = [];
      let done = 0;

      for (const chunk of chunkRows(updateData.p_data, IMPORT_CHUNK_ROWS.contracts)) {
        let result: ManageImportContractsResultType;
        try {
          result = await manageImportContractsAction({ ...updateData, p_data: chunk });
        } catch (error) {
          if (results.length === 0) throw error;
          return mergeContractsResults(results, offsets, { interrupted: INTERRUPTED_MESSAGE, rowsNotRun: total - done });
        }

        if (result.refused) {
          if (results.length === 0) return result;
          return mergeContractsResults(results, offsets, {
            refused: result.refused,
            usage: result.usage,
            block: result.block,
            rowsNotRun: total - done,
          });
        }

        results.push(result);
        offsets.push(done);
        done += chunk.length;
        options?.onProgress?.(done, total);
      }

      return mergeContractsResults(results, offsets); 
    },
    onSuccess: () => {
      // Success is handled by navigation to results screen
      // No toast needed
    },
    onError: (error: Error) => {
      handleHookError(showToast, error, "Hubo un error al importar los contratos.");
    },
  });
};

/**
 * Hook to get the static data for the contract details
 * @param languageCode - The language code to get the static data for
 * @returns The static data for the contract details
 */
export const useImportStatic = (languageCode: string) => {
  const ratePeriod = useRatePeriodStatic(languageCode);
  const invoiceTimeMode = useInvoiceTimeModeStatic(languageCode);
  const invoiceMode = useInvoiceModeStatic(languageCode);
  const invoiceFrequency = useInvoiceFrequencyStatic(languageCode);
  const gender = useGenderStatic(languageCode);
  const countries = useCountriesStatic(languageCode);
  // The plan limit of time slots per day of an imported contract (null: no limit).
  const planInfo = useSubscriptionPlanInfo();

  const isLoading = [
    ratePeriod.isLoading,
    invoiceTimeMode.isLoading,
    invoiceMode.isLoading,
    invoiceFrequency.isLoading,
    gender.isLoading,
    countries.isLoading,
  ].some(Boolean);

  const isError = [
    ratePeriod.isError,
    invoiceTimeMode.isError,
    invoiceMode.isError,
    invoiceFrequency.isError,
    gender.isError,
    countries.isError,
  ].some(Boolean);

  const isSuccess = [
    ratePeriod.isSuccess,
    invoiceTimeMode.isSuccess,
    invoiceMode.isSuccess,
    invoiceFrequency.isSuccess,
    gender.isSuccess,
    countries.isSuccess,
  ].every(Boolean);

  return {
    ratePeriod: { data: ratePeriod.data || [] },
    invoiceTimeMode: { data: invoiceTimeMode.data || [] },
    invoiceMode: { data: invoiceMode.data || [] },
    invoiceFrequency: { data: invoiceFrequency.data || [] },
    gender: { data: gender.data || [] },
    countries: { data: countries.data || [] },
    scheduleSlotsPerDay: planInfo.data?.limits?.import_schedule_demand_slots_per_day ?? null,
    isLoading,
    isError,
    isSuccess,
  };
};




